// SlaFlag — the QUEUE's SLA cell. Breach flags only, never a countdown.
//
// Translated from prototype/primitives.jsx `SlaFlag`.
//
// The list DTO originally carried only the two breach booleans, so this atom
// was breach-only. `firstResponseDueAt` / `resolutionDueAt` have since been
// added to TicketListRow (#136 A1), so an unbreached row can now show how long
// is left rather than a flat "On track".
//
// 🔒 STILL NOT RENDERED HERE: the at-risk *state*. The design fires at-risk at
// the SLA policy's `warningPct`, and the queue does not load the policy — a
// row knows its deadline but not the threshold its desk considers "close".
// Picking a threshold here (75%? 90%?) would be inventing policy and would
// silently disagree with the ticket detail, which uses the real one. Amber
// therefore stays exclusively for breach until the policy is available.
import React from 'react';
import { Box } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

function BreachTag({ children }: { children: React.ReactNode }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '2px 6px',
        borderRadius: '4px',
        backgroundColor: 'var(--sla-breach-bg)',
        color: 'var(--sla-breach)',
        fontFamily: 'var(--font-mono)',
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '.04em',
        lineHeight: 1.6,
        whiteSpace: 'nowrap'
      }}
    >
      <ErrorOutlineIcon sx={{ fontSize: 11 }} />
      {children}
    </Box>
  );
}

/**
 * Compact "time left" for the nearest outstanding deadline, e.g. "2h left",
 * "3d left". Minutes below an hour, hours below a day, days beyond that —
 * a queue cell has no room for more precision than the decision needs.
 * Returns null when there is no future deadline to describe.
 */
function timeLeft(dueAt: string | null | undefined): string | null {
  if (!dueAt) return null;
  const ms = new Date(dueAt).getTime() - Date.now();
  if (!Number.isFinite(ms) || ms <= 0) return null;
  const minutes = Math.floor(ms / 60000);
  if (minutes < 60) return `${minutes}m left`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h left`;
  return `${Math.floor(hours / 24)}d left`;
}

interface SlaFlagProps {
  /** TicketListRow.firstResponseBreached */
  firstResponseBreached: boolean;
  /** TicketListRow.resolutionBreached */
  resolutionBreached: boolean;
  /** TicketListRow.firstResponseDueAt */
  firstResponseDueAt?: string | null;
  /** TicketListRow.resolutionDueAt */
  resolutionDueAt?: string | null;
  /** No SLA policy applies to this ticket — renders an em dash. */
  none?: boolean;
}

export default function SlaFlag({
  firstResponseBreached,
  resolutionBreached,
  firstResponseDueAt,
  resolutionDueAt,
  none
}: SlaFlagProps) {
  if (none) {
    return (
      <Box component="span" sx={{ fontSize: 12, color: 'var(--muted-2)' }}>
        —
      </Box>
    );
  }

  if (!firstResponseBreached && !resolutionBreached) {
    // Nearest outstanding deadline. First response is checked first because it
    // is the one that comes due first whenever both are set.
    const remaining = timeLeft(firstResponseDueAt) || timeLeft(resolutionDueAt);
    return (
      <Box
        component="span"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: 12, color: 'var(--muted)' }}
      >
        <Box
          component="span"
          sx={{ width: 6, height: 6, borderRadius: '3px', backgroundColor: 'var(--sla-ok)', flexShrink: 0 }}
        />
        {remaining ? (
          <Box component="span" sx={{ fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}>
            {remaining}
          </Box>
        ) : (
          'On track'
        )}
      </Box>
    );
  }

  return (
    <Box component="span" sx={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px' }}>
      {firstResponseBreached ? <BreachTag>FIRST REPLY</BreachTag> : null}
      {resolutionBreached ? <BreachTag>RESOLUTION</BreachTag> : null}
    </Box>
  );
}
